import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, MapPin, Award, Languages, Clock, DollarSign, Video, Phone, Calendar, Check, Shield, MessageCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { doctors } from '../mockData';

const DoctorProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const doctor = doctors.find(d => d.id === parseInt(id));
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [bookingType, setBookingType] = useState('clinic'); // 'clinic' or 'video'
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!doctor) {
    return (
      <div style={styles.pageWrapper}>
        <Navbar />
        <div style={styles.notFound}>
          <h2>Doctor not found</h2>
          <button onClick={() => navigate('/find-doctors')} style={styles.backButton}>
            Back to Search
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const handleBooking = () => {
    if (!selectedDate || !selectedTime) {
      alert('Please select a date and time for your appointment');
      return;
    }
    
    alert(`Booking confirmed!\n\nDoctor: ${doctor.name}\nType: ${bookingType === 'video' ? 'Video Consultation' : 'In-Person Visit'}\nDate: ${selectedDate}\nTime: ${selectedTime}\nFee: $${doctor.consultationFee}\n\nYou will receive a confirmation email shortly.`);
  };

  // Dynamic styles for responsive layout
  const dynamicStyles = {
    headerGrid: isMobile ? styles.headerGrid : {
      ...styles.headerGrid,
      flexDirection: 'row',
      textAlign: 'left',
      maxWidth: '1000px',
      margin: '0 auto'
    },
    doctorPhotoWrapper: isMobile ? styles.doctorPhotoWrapper : {
      ...styles.doctorPhotoWrapper,
      maxWidth: '300px',
      minWidth: '280px'
    },
    doctorInfo: isMobile ? styles.doctorInfo : {
      ...styles.doctorInfo,
      flex: '1',
      textAlign: 'left'
    },
    ratingRow: isMobile ? styles.ratingRow : {
      ...styles.ratingRow,
      justifyContent: 'flex-start'
    },
    quickInfoGrid: isMobile ? styles.quickInfoGrid : {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '16px',
      width: '100%'
    },
    quickInfoItem: isMobile ? styles.quickInfoItem : {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px',
      background: '#FFFFFF',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
      textAlign: 'left'
    },
    contentGrid: isMobile ? styles.contentGrid : {
      display: 'grid',
      gridTemplateColumns: '1fr 380px',
      gap: '32px',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    bookingCard: isMobile ? styles.bookingCard : {
      ...styles.bookingCard,
      position: 'sticky',
      top: '100px'
    },
    bookingTypeSelector: isMobile ? styles.bookingTypeSelector : {
      display: 'flex',
      gap: '12px',
      marginBottom: '24px'
    }
  };

  return (
    <div style={styles.pageWrapper}>
      <Navbar />
      
      {/* Back Button */}
      <div style={styles.backNav}>
        <button onClick={() => navigate('/find-doctors')} style={styles.backBtn}>
          <ArrowLeft size={20} />
          <span>Back to Search</span>
        </button>
      </div>

      {/* Doctor Header */}
      <section style={styles.headerSection}>
        <div style={styles.container}>
          <div style={dynamicStyles.headerGrid}>
            <div style={dynamicStyles.doctorPhotoWrapper}>
              <img src={doctor.photo} alt={doctor.name} style={styles.doctorPhoto} />
              <div style={styles.verifiedBadge}>
                <Shield size={16} />
                <span>Verified</span>
              </div>
            </div>

            <div style={dynamicStyles.doctorInfo}>
              <div style={styles.nameSection}>
                <h1 style={styles.doctorName}>{doctor.name}</h1>
                <div style={dynamicStyles.ratingRow}>
                  <div style={styles.ratingBadge}>
                    <Star size={18} fill="#FFD700" color="#FFD700" />
                    <span style={styles.ratingText}>{doctor.rating}</span>
                  </div>
                  <span style={styles.reviewCount}>({doctor.reviewCount} reviews)</span>
                </div>
              </div>

              <p style={styles.specialty}>{doctor.specialty}</p>
              <p style={styles.qualifications}>{doctor.qualifications}</p>

              <div style={dynamicStyles.quickInfoGrid}>
                <div style={dynamicStyles.quickInfoItem}>
                  <Award size={20} color="#1D57D5" />
                  <div>
                    <p style={styles.quickInfoLabel}>Experience</p>
                    <p style={styles.quickInfoValue}>{doctor.experience} years</p>
                  </div>
                </div>
                <div style={dynamicStyles.quickInfoItem}>
                  <MapPin size={20} color="#28C08A" />
                  <div>
                    <p style={styles.quickInfoLabel}>Distance</p>
                    <p style={styles.quickInfoValue}>{doctor.distance} away</p>
                  </div>
                </div>
                <div style={dynamicStyles.quickInfoItem}>
                  <DollarSign size={20} color="#F4CE7B" />
                  <div>
                    <p style={styles.quickInfoLabel}>Consultation Fee</p>
                    <p style={styles.quickInfoValue}>${doctor.consultationFee}</p>
                  </div>
                </div>
                <div style={dynamicStyles.quickInfoItem}>
                  <Languages size={20} color="#1D57D5" />
                  <div>
                    <p style={styles.quickInfoLabel}>Languages</p>
                    <p style={styles.quickInfoValue}>{doctor.languages.join(', ')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section style={styles.mainContent}>
        <div style={styles.container}>
          <div style={dynamicStyles.contentGrid}>
            {/* Left Column: Details */}
            <div style={styles.leftColumn}>
              {/* About Section */}
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>About {doctor.name.split(' ')[1]}</h2>
                <p style={styles.aboutText}>{doctor.about}</p>
              </div>

              {/* Clinic Info */}
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>Clinic Information</h2>
                <div style={styles.clinicInfo}>
                  <MapPin size={20} color="#1D57D5" />
                  <div>
                    <p style={styles.clinicName}>{doctor.clinicName}</p>
                    <p style={styles.clinicAddress}>{doctor.clinicAddress}</p>
                  </div>
                </div>
              </div>

              {/* Insurance Accepted */}
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>Insurance Accepted</h2>
                <div style={styles.insuranceGrid}>
                  {doctor.acceptsInsurance.map((insurance, index) => (
                    <div key={index} style={styles.insuranceTag}>
                      <Check size={14} color="#28C08A" />
                      <span>{insurance}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reviews Section */}
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>Patient Reviews</h2>
                <div style={styles.reviewsWrapper}>
                  {doctor.reviews.map(review => (
                    <div key={review.id} style={styles.reviewCard}>
                      <div style={styles.reviewHeader}>
                        <div style={styles.reviewerInfo}>
                          <div style={styles.reviewerAvatar}>
                            {review.patient.charAt(0)}
                          </div>
                          <div>
                            <p style={styles.reviewerName}>{review.patient}</p>
                            <p style={styles.reviewDate}>{review.date}</p>
                          </div>
                        </div>
                        <div style={styles.reviewRating}>
                          {[...Array(review.rating)].map((_, i) => (
                            <Star key={i} size={14} fill="#FFD700" color="#FFD700" />
                          ))}
                        </div>
                      </div>
                      <p style={styles.reviewComment}>{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Booking */}
            <div style={styles.rightColumn}>
              <div style={dynamicStyles.bookingCard}>
                <h2 style={styles.bookingTitle}>Book Appointment</h2>

                {/* Booking Type Selection */}
                <div style={dynamicStyles.bookingTypeSelector}>
                  <button
                    onClick={() => setBookingType('clinic')}
                    style={{
                      ...styles.typeButton,
                      ...(bookingType === 'clinic' ? styles.typeButtonActive : {})
                    }}
                  >
                    <Phone size={18} />
                    <span>In-Person Visit</span>
                  </button>
                  {doctor.videoConsultation && (
                    <button
                      onClick={() => setBookingType('video')}
                      style={{
                        ...styles.typeButton,
                        ...(bookingType === 'video' ? styles.typeButtonActive : {})
                      }}
                    >
                      <Video size={18} />
                      <span>Video Consultation</span>
                    </button>
                  )}
                </div>

                {/* Available Slots */}
                <div style={styles.slotsSection}>
                  <h3 style={styles.slotsTitle}>
                    <Clock size={18} />
                    Available Slots
                  </h3>

                  {/* Date Selection */}
                  <div style={styles.dateSelection}>
                    <label style={styles.inputLabel}>Select Date</label>
                    <select
                      value={selectedDate}
                      onChange={(e) => {
                        setSelectedDate(e.target.value);
                        setSelectedTime(''); // Reset time when date changes
                      }}
                      style={styles.selectInput}
                    >
                      <option value="">Choose a date</option>
                      {[...new Set(doctor.availableSlots.map(slot => slot.date))].map(date => (
                        <option key={date} value={date}>{date}</option>
                      ))}
                    </select>
                  </div>

                  {/* Time Selection */}
                  {selectedDate && (
                    <div style={styles.timeSelection}>
                      <label style={styles.inputLabel}>Select Time</label>
                      <div style={styles.timeSlots}>
                        {doctor.availableSlots
                          .filter(slot => slot.date === selectedDate)
                          .map((slot, index) => (
                            <button
                              key={index}
                              onClick={() => setSelectedTime(slot.time)}
                              style={{
                                ...styles.timeSlot,
                                ...(selectedTime === slot.time ? styles.timeSlotActive : {})
                              }}
                            >
                              {slot.time}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Fee Summary */}
                <div style={styles.feeSummary}>
                  <div style={styles.feeRow}>
                    <span style={styles.feeLabel}>Consultation Fee:</span>
                    <span style={styles.feeValue}>${doctor.consultationFee}</span>
                  </div>
                  {bookingType === 'video' && (
                    <div style={styles.videoNote}>
                      <Video size={14} />
                      <span>Video consultation via secure platform</span>
                    </div>
                  )}
                </div>

                {/* Book Button */}
                <button
                  onClick={handleBooking}
                  disabled={!selectedDate || !selectedTime}
                  style={{
                    ...styles.bookButton,
                    ...(!selectedDate || !selectedTime ? styles.bookButtonDisabled : {})
                  }}
                >
                  <Calendar size={18} />
                  <span>Confirm Booking</span>
                </button>

                {/* Help Text */}
                <p style={styles.helpText}>
                  <MessageCircle size={14} />
                  Need help? Contact our support team
                </p>
              </div>

              {/* Quick Actions */}
              <div style={styles.quickActions}>
                <button style={styles.actionButton}>
                  <Phone size={18} />
                  <span>Call Clinic</span>
                </button>
                <button style={styles.actionButton}>
                  <MapPin size={18} />
                  <span>Get Directions</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  pageWrapper: {
    minHeight: '100vh',
    backgroundColor: '#F8FAFC',
    overflowX: 'hidden'
  },
  backNav: {
    padding: '16px 24px',
    background: '#FFFFFF',
    borderBottom: '1px solid #E2E8F0'
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    background: 'transparent',
    border: '2px solid #E2E8F0',
    borderRadius: '12px',
    color: '#64748B',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  headerSection: {
    padding: '40px 16px',
    background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)'
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 16px',
    boxSizing: 'border-box'
  },
  headerGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    alignItems: 'center',
    textAlign: 'center'
  },
  doctorPhotoWrapper: {
    position: 'relative',
    width: '100%',
    maxWidth: '280px'
  },
  doctorPhoto: {
    width: '100%',
    height: 'auto',
    aspectRatio: '1 / 1.2',
    objectFit: 'cover',
    borderRadius: '20px',
    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)'
  },
  verifiedBadge: {
    position: 'absolute',
    bottom: '16px',
    left: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: 'rgba(40, 192, 138, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '50px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: '600',
    boxShadow: '0 4px 12px rgba(40, 192, 138, 0.3)'
  },
  doctorInfo: {
    paddingTop: '0',
    width: '100%'
  },
  nameSection: {
    marginBottom: '16px'
  },
  doctorName: {
    fontSize: 'clamp(24px, 6vw, 42px)',
    fontWeight: '800',
    color: '#1A202C',
    marginBottom: '12px',
    lineHeight: '1.2',
    wordBreak: 'break-word'
  },
  ratingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap'
  },
  ratingBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: '#FFFFFF',
    borderRadius: '50px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
  },
  ratingText: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1A202C'
  },
  reviewCount: {
    fontSize: '16px',
    color: '#64748B'
  },
  specialty: {
    fontSize: 'clamp(16px, 4vw, 20px)',
    fontWeight: '600',
    color: '#1D57D5',
    marginBottom: '8px'
  },
  qualifications: {
    fontSize: 'clamp(14px, 3.5vw, 16px)',
    color: '#64748B',
    marginBottom: '24px',
    lineHeight: '1.6'
  },
  quickInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    width: '100%'
  },
  quickInfoItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    background: '#FFFFFF',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
    textAlign: 'center'
  },
  quickInfoLabel: {
    fontSize: '12px',
    color: '#64748B',
    marginBottom: '2px'
  },
  quickInfoValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1A202C'
  },
  mainContent: {
    padding: '32px 16px'
  },
  contentGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%'
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    width: '100%'
  },
  card: {
    background: '#FFFFFF',
    padding: '20px 16px',
    borderRadius: '16px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)'
  },
  cardTitle: {
    fontSize: 'clamp(18px, 5vw, 24px)',
    fontWeight: '700',
    color: '#1A202C',
    marginBottom: '16px'
  },
  aboutText: {
    fontSize: '15px',
    color: '#64748B',
    lineHeight: '1.7'
  },
  clinicInfo: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap'
  },
  clinicName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1A202C',
    marginBottom: '4px'
  },
  clinicAddress: {
    fontSize: '14px',
    color: '#64748B',
    lineHeight: '1.5'
  },
  insuranceGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  insuranceTag: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: '#E8F5F1',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#1A202C',
    fontWeight: '500'
  },
  reviewsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  reviewCard: {
    padding: '16px',
    background: '#F8FAFC',
    borderRadius: '12px',
    border: '1px solid #E2E8F0'
  },
  reviewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
    flexWrap: 'wrap',
    gap: '8px'
  },
  reviewerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  reviewerAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #1D57D5 0%, #133E97 100%)',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '600'
  },
  reviewerName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1A202C'
  },
  reviewDate: {
    fontSize: '13px',
    color: '#64748B'
  },
  reviewRating: {
    display: 'flex',
    gap: '2px'
  },
  reviewComment: {
    fontSize: '14px',
    color: '#64748B',
    lineHeight: '1.6'
  },
  bookingCard: {
    background: '#FFFFFF',
    padding: '24px 16px',
    borderRadius: '16px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
    position: 'relative',
    width: '100%'
  },
  bookingTitle: {
    fontSize: 'clamp(18px, 5vw, 24px)',
    fontWeight: '700',
    color: '#1A202C',
    marginBottom: '20px'
  },
  bookingTypeSelector: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '20px'
  },
  typeButton: {
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '14px 12px',
    background: '#F8FAFC',
    border: '2px solid #E2E8F0',
    borderRadius: '12px',
    color: '#64748B',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    width: '100%'
  },
  typeButtonActive: {
    background: '#E3EDFF',
    borderColor: '#1D57D5',
    color: '#1D57D5'
  },
  slotsSection: {
    marginBottom: '20px'
  },
  slotsTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#1A202C',
    marginBottom: '12px'
  },
  dateSelection: {
    marginBottom: '12px'
  },
  inputLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#1A202C',
    marginBottom: '6px'
  },
  selectInput: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid #E2E8F0',
    borderRadius: '12px',
    fontSize: '14px',
    color: '#1A202C',
    background: '#FFFFFF',
    cursor: 'pointer',
    outline: 'none',
    boxSizing: 'border-box'
  },
  timeSelection: {
    marginTop: '12px'
  },
  timeSlots: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px'
  },
  timeSlot: {
    padding: '10px',
    background: '#F8FAFC',
    border: '2px solid #E2E8F0',
    borderRadius: '10px',
    color: '#64748B',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  timeSlotActive: {
    background: '#E3EDFF',
    borderColor: '#1D57D5',
    color: '#1D57D5'
  },
  feeSummary: {
    padding: '20px',
    background: '#F8FAFC',
    borderRadius: '12px',
    marginBottom: '20px'
  },
  feeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  feeLabel: {
    fontSize: '15px',
    color: '#64748B'
  },
  feeValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1A202C'
  },
  videoNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px',
    background: '#E3EDFF',
    borderRadius: '8px',
    color: '#1D57D5',
    fontSize: '13px',
    fontWeight: '500'
  },
  bookButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '16px',
    background: 'linear-gradient(135deg, #1D57D5 0%, #133E97 100%)',
    border: 'none',
    borderRadius: '12px',
    color: '#FFFFFF',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginBottom: '16px'
  },
  bookButtonDisabled: {
    opacity: '0.5',
    cursor: 'not-allowed'
  },
  helpText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#64748B',
    textAlign: 'center'
  },
  quickActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '14px',
    background: '#FFFFFF',
    border: '2px solid #E2E8F0',
    borderRadius: '12px',
    color: '#64748B',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  notFound: {
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px'
  },
  backButton: {
    padding: '12px 32px',
    background: 'linear-gradient(135deg, #1D57D5 0%, #133E97 100%)',
    border: 'none',
    borderRadius: '50px',
    color: '#FFFFFF',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default DoctorProfile;
